"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import Icon from "@/components/ui/AppIcon";

interface NewsletterFormProps {
  variant?: "footer" | "hero";
}

export default function NewsletterForm({ variant = "footer" }: NewsletterFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");

    // Email validation
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!email || !emailRegex.test(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address");
      return;
    }

    try {
      // Insert into database
      const { error: dbError } = await supabase
        .from("newsletter_subscribers")
        .insert({ email })
        .select();

      if (dbError) {
        if (dbError.code === "23505") {
          // Unique constraint violation - already subscribed
          setStatus("error");
          setMessage("This email is already subscribed!");
          return;
        }
        throw dbError;
      }

      // Call edge function to send confirmation email
      const { error: functionError } = await supabase.functions.invoke(
        "newsletter-subscribe",
        {
          body: { email },
        }
      );

      if (functionError) {
        console.error("Email sending error:", functionError);
        // Still show success since database insert worked
      }

      setStatus("success");
      setMessage("🎉 Successfully subscribed! Check your email for confirmation.");
      setEmail("");

      // Reset after 5 seconds
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 5000);
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      setStatus("error");
      setMessage(error?.message || "Failed to subscribe. Please try again.");
    }
  };

  const isFooter = variant === "footer";

  return (
    <div className={isFooter ? "" : "max-w-md mx-auto lg:mx-0"}>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Icon
            name="EnvelopeIcon"
            size={20}
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${isFooter ? "text-white/40" : "text-plum/40"}`}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={status === "loading"}
            className={
              isFooter
                ? "w-full pl-10 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-50" :"w-full pl-10 pr-4 py-3 rounded-full bg-white border-2 border-lavender/30 text-plum placeholder-plum/40 focus:outline-none focus:ring-2 focus:ring-lavender focus:border-lavender disabled:opacity-50"
            }
            required
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className={
            isFooter
              ? "px-6 py-3 rounded-full bg-white text-plum font-bold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px]"
              : "px-6 py-3 rounded-full bg-plum text-white font-bold hover:bg-plum-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px]"
          }
        >
          {status === "loading" ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Subscribing...
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={18} variant="solid" />
              Subscribe
            </>
          )}
        </button>
      </form>

      {message && (
        <div
          className={`mt-3 p-3 rounded-lg text-sm ${
            status === "success"
              ? isFooter
                ? "bg-green-500/20 text-white border border-green-500/30" :"bg-green-50 text-green-700 border border-green-200"
              : isFooter
              ? "bg-red-500/20 text-white border border-red-500/30" :"bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}